/**
 * Order — the single money aggregate, born authorised at settle. Status is
 * monotonic, advanced only by CAS inside transactions.
 *
 * Path: `workspaces/{orgId}/events/{eventId}/orders/{orderId}` — random id
 * (no lexicographic hotspots). Buyer reads and listens by
 * `buyerUid == auth.uid`; a guest order's issuing member reads their own
 * issuance the same way, by `issuedBy == auth.uid` — get-only, same as the
 * buyer clause, since `tickets.guests.issue` carries no read/list authority
 * of its own; member read behind `tickets.reports.view`. A member holding
 * `tickets.orders.amend` also holds a `buyerEmail` fix-email
 * write rule (audited `updatedBy`, masked to those two fields) — a hand fragment
 * in root `package.json`'s `ican.extraMatches`, since the replacement value
 * is operator-chosen, not a fixed literal `.writes(...)` can pin. Amend is
 * deliberately its own token, not `tickets.reports.view`: mutating a
 * buyer's contact details is not a read, and a reports-viewer holding it
 * only for sales-dashboard visibility gets no PII-write power by riding
 * along.
 *
 * `workspaces/{orgId}/events/{eventId}/orders/{orderId}/deliveries/{task}`
 * carries the fire-once delivery tasks, one marker doc per task —
 * `DeliveryTaskNameSchema` names the set, `DeliveryTaskSchema` below is the
 * marker shape. Member read (lookup) here; the email resend
 * (`sendNumber + 1`, `status: 'pending'` on the `email` task doc, so
 * `fulfillmentSweep` picks it up) is a second `ican.extraMatches` hand
 * fragment — the increment is relative to the doc's own pre-write
 * `sendNumber`, which `.writes(...)`'s literal/token-only value map
 * cannot express either.
 *
 * `LocaleSchema`, `ChannelSchema`, `PspProviderSchema`, and `PaymentModeSchema`
 * are declared here and reused by `checkout.entity.ts` / `workspace.entity.ts`
 * — the same vocabulary every buyer-facing artifact and the order itself
 * share. `LocaleSchema` itself derives from the `@momo/i18n` seam's
 * `LocaleEnum` — the single source for the two-token UI-language vocabulary —
 * rather than re-listing `['de', 'en']`.
 */

import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import { LocaleEnum } from "@momo/i18n";
import type { Timestamp } from "firebase/firestore";
import { z } from "zod";
import { OrderOriginSchema } from "./order-origin.const.ts";
import { SecretToken, type Ticket } from "./ticket.entity.ts";
import { Cents, PositiveCents, Qty } from "./ticket-type.entity.ts";
import type { Actor } from "./workspace.entity.ts";

// ── Shared vocabulary ──────────────────────────────────────────────────────
export const LocaleSchema = LocaleEnum.describe(
	"the language every buyer-facing artifact renders in — detected from the browser, stored when the checkout starts",
);
export const OrderStatusSchema = z
	.enum(["authorised", "paid", "refunded", "cancelled"])
	.describe(
		"the order state machine — monotonic, advanced only by CAS inside transactions; cancelled is reached only by a capture hard-fail or a post-settle void, never by a decline, since a pre-settle decline never creates an order at all",
	);
export const DeliveryTaskStatusSchema = z.enum(["pending", "done", "failed"]);
export const DeliveryStatusSchema = z
	.enum(["unknown", "delivered", "opened", "hard_bounced"])
	.describe(
		"what Lettermint last reported about the ticket email — written as `unknown` at send, never left absent, so the 24 h status poll is a single equality query rather than a filter Firestore cannot compound",
	);
export type DeliveryStatus = z.infer<typeof DeliveryStatusSchema>;

/**
 * The ladder this status only ever climbs. Lettermint fans each event out to
 * every registered URL and guarantees no ordering between them, so
 * `message.opened` can land before the `message.delivered` for the same send —
 * a blind write would drop the buyer's two ticks back to one. lettermintWebhook
 * writes only when the arriving rank EXCEEDS the stored one, which makes the
 * displayed state independent of arrival order.
 *
 * `hard_bounced` sits at the top rather than beside `delivered`: it is the one
 * value an operator must act on, so no late event may mask it. The two are
 * mutually exclusive in practice anyway — a hard bounce is a permanent
 * rejection at the receiving server, so a message that hard-bounced never
 * reached a mailbox to be opened from.
 */
export const DELIVERY_STATUS_RANK: Record<DeliveryStatus, number> = {
	unknown: 0,
	delivered: 1,
	opened: 2,
	hard_bounced: 3,
};
export const PspProviderSchema = z
	.enum(["revolut", "stripe"])
	.describe(
		"the payment providers the platform’s adapter seam supports — which one a workspace runs is set on the public workspace card, never a tracked literal",
	);
export const PaymentModeSchema = z
	.enum(["sandbox", "live"])
	.describe(
		"a Revolut environment — sandbox for test-mode credentials and traffic, live for real money — the same two-token vocabulary the deployment seam, the runtime active mode, and a settled order's own stamped environment all share",
	);
export type PaymentMode = z.infer<typeof PaymentModeSchema>;

export const ChannelSchema = z
	.object({
		source: z.string().min(1).nullish(),
		utm: z.record(z.string(), z.string()).nullish(),
	})
	.describe(
		"the marketing channel that delivered this buyer — captured once, from the page’s own UTM params, at whichever write actually lands the buyer here (checkout create or restart); never recaptured by a later items/contact edit, and absent entirely on a resumed live session, which performs no write at all",
	);

// Declared here, ahead of the order itself, because CheckoutSchema's
// pricedLines needs it too — the same shape both documents use; one line
// schema, two documents.
/** § 33 UStDV's Kleinbetragsrechnung ceiling: at or below this GROSS total an
 *  invoice needs no recipient at all, and above it § 14 Abs. 4 Nr. 1 requires
 *  the recipient's full name AND address — which is the buyer's own interest,
 *  since without the address their Vorsteuerabzug can be refused. One number,
 *  shared by the checkout that asks for the address, the settle that refuses
 *  without it, and the invoice that prints it. */
export const KLEINBETRAG_CEILING_CENTS = 25_000;

/** Whether an order of this gross total must carry a recipient address. */
export function needsRecipientAddress(grossCents: number): boolean {
	return grossCents > KLEINBETRAG_CEILING_CENTS;
}

/** `RE-{year}-{6-digit}` — year from `issuedAt` (the pinned issuance moment,
 *  never the render moment), so a re-drive that reuses a pinned number never
 *  recomputes a different year. Read in the VENUE's zone, the same one the
 *  document prints its issue date in: a capture at 00:30 Berlin on New Year's
 *  Day is 23:30 UTC on New Year's Eve, and a number whose year disagrees with
 *  the issue date printed beside it files the document in the wrong tax
 *  year. */
export function formatInvoiceNumber(
	next: number,
	issuedAt: Date,
	timezone: string,
): string {
	const year = new Intl.DateTimeFormat("en-CA", {
		timeZone: timezone,
		year: "numeric",
	}).format(issuedAt);
	return `RE-${year}-${String(next).padStart(6, "0")}`;
}

// The supply itself — a quantity of one ticket type at the price the checkout
// fixed for it. The only line kind that mints anything scannable, and the only
// one carrying a typeId or a qty, which is why every reader that reaches for
// either narrows to this branch first.
//
// `kind` is optional on the way IN and always present on the way out. Every
// line written before the order carried more than one kind of line omits the
// field, and those documents are read at some thirty `OrderSchema.safeParse`
// seams — every delivery, refund, receipt and console read — each of which
// drops an order it cannot parse. The optional discriminant is what carries
// them: `z.discriminatedUnion` builds its option map from the discriminant's
// own accepted values, and `.optional()` puts `undefined` in that map, so a
// line with no `kind` dispatches to this branch by the discriminant rather
// than by trying branches until one fits, and the default stamps it `ticket`
// before any reader sees it. Absent `kind` IS `ticket` — that is a fact about
// how every line was encoded up to the moment a second kind existed, declared
// here rather than adapted around at each seam or rewritten into settled
// financial records.
export const TicketLineSchema = z.object({
	kind: z
		.literal("ticket")
		.optional()
		.default("ticket")
		.describe(
			"marks the line as the admission itself — omitted by every line written while a ticket was the only thing an order could carry, and read as `ticket` for exactly that reason",
		),
	typeId: z.string(),
	unitPrice: Cents.describe(
		"the price fixed when the checkout prices its lines — immutable; refunds price from here, never the live catalog",
	),
	qty: Qty,
	vatRate: z.number().min(0).max(100),
	amount: Cents.describe(
		"the line’s charged total after discounts — not necessarily unitPrice × qty",
	),
});
export type TicketLine = z.infer<typeof TicketLineSchema>;
// kind carries a .default(), so a writer building a ticket line and letting
// the schema stamp the discriminant types its literal against this input
// shape, not the output above.
export type TicketLineInput = z.input<typeof TicketLineSchema>;

// The operator's own charge for selling the admission, split across the VAT
// rates of the admissions it is ancillary to (booking-fee.entity.ts's
// splitFeeByRate is its only writer). The buyer pays it to the operator and
// the invoice itemises it — the opposite direction from the `fees` FeeEntry
// array further down, which is what the payment provider kept and the buyer
// never sees.
// Fields justified:
//   kind    — the discriminant
//   vatRate — the admission rate this part of the charge is charged at
//   amount  — the charge, gross, at that rate
// Excluded:
//   typeId / qty — a booking fee buys no seat: it mints no ticket and is
//     bought in no quantity, and its absence here is exactly what ticket
//     minting and perTicketShares narrow it out on
export const BookingFeeLineSchema = z.object({
	kind: z.literal("bookingFee"),
	vatRate: z.number().min(0).max(100),
	amount: PositiveCents.describe(
		"the booking fee charged at this VAT rate, gross — a rate whose apportioned share rounds away carries no line at all, so this is never zero",
	),
});
export type BookingFeeLine = z.infer<typeof BookingFeeLineSchema>;

// The line a voucher's discount takes off, one per distinct VAT rate among the
// lines it applies to — § 10 Abs. 1 UStG makes a Rabatt reduce the Entgelt of
// the supplies it relates to, so a mixed 7 %/19 % basket splits rather than
// folding down to one figure, and `vatCents` derives the reduction per rate
// with no case of its own. Written by settle out of `resolveVoucher`
// (voucher.entity.ts), which is the one place the figures are computed.
// Fields justified:
//   code    — which code took this off, legible on the order itself
//   vatRate — the rate of the supplies this part of the discount reduces
//   amount  — the reduction, gross, at that rate
// Excluded:
//   typeId / unitPrice / qty — a discount is not a supply: it buys nothing,
//     mints no ticket, and apportions across rates rather than across types
export const DiscountLineSchema = z.object({
	kind: z.literal("discount"),
	code: z.string(),
	vatRate: z.number().min(0).max(100),
	amount: z
		.number()
		.int()
		.negative()
		.describe("cents off, at this VAT rate — always negative"),
});
export type DiscountLine = z.infer<typeof DiscountLineSchema>;

// The order's money lines, discriminated on `kind`. Every branch declares its
// own fields as required, so a booking-fee line with a typeId or a ticket line with no
// qty is unrepresentable, dispatch is a single map lookup rather than a walk
// through every shape, and a parse failure reports against the one branch the
// discriminant chose instead of against all three at once.
//
// Only `vatRate` and `amount` are common to every branch, which is what makes
// every `Σ lines[].amount` and every VAT extraction in this file kind-blind by
// construction while every reader of `typeId` or `qty` must say which lines it
// means.
export const OrderLineSchema = z.discriminatedUnion("kind", [
	TicketLineSchema,
	BookingFeeLineSchema,
	DiscountLineSchema,
]);
export type OrderLine = z.infer<typeof OrderLineSchema>;
export type OrderLineInput = z.input<typeof OrderLineSchema>;

// A reader that needs one kind of line narrows on the discriminant itself —
// `lines.filter((line) => line.kind === "ticket")` narrows to TicketLine, here
// and at every call site. A parsed line always carries the discriminant: the
// ticket branch's own default is what guarantees it for a line written before
// the discriminant existed, so no reader has to know that history.

export const FeeEntrySchema = z.object({
	type: z
		.string()
		.describe(
			"the provider's own fee-kind vocabulary, stored verbatim — Revolut reports acquiring and fx, Stripe's fee_details types differ, so this is an open foreign vocabulary and deliberately never a closed enum of ours",
		),
	amountCents: Cents.describe(
		"what this entry cost the organiser, in integer minor units",
	),
	currency: z
		.string()
		.describe(
			"the entry's ISO currency code — the adapter refuses (returns null) any report holding an entry whose currency differs from the order's, so a stored array is single-currency by construction",
		),
});
export type FeeEntry = z.infer<typeof FeeEntrySchema>;

// events/{eventId}/orders/{orderId} — random id (no lexicographic hotspots)
export const OrderSchema = z.object({
	status: OrderStatusSchema,
	origin: OrderOriginSchema.describe(
		"what created this order — the buyer’s own checkout (sale) or a member’s issuance (guest); it names the path the order arrived by, never whether money moved, since a €0 basket also arrives as a sale",
	),
	// Excluded: eventId — Order nests under its event (events/{id}/orders/{orderId});
	// the refund consumer's and the capture-hard-fail compensation's give-back, and
	// every per-event aggregate (revenue, VAT, channel), read it off the document's
	// own path instead of a stored copy.
	// Excluded: a location/venue copy — the PDF, .ics, and wallet artifacts resolve
	// event.locationId (or the ticket type's own locationId override) against the
	// location catalog live at fulfillment time; no location copy travels onto the order.
	// Immutable once settled: fulfillTickets numbers ticket ids off this
	// array's own index order (t__{orderId}__{n}), so a future refund
	// feature mutating lines in place — reordering, splicing a removal — would
	// silently desync that numbering from the tickets already minted. That
	// same numbering is why a line carrying no ticket is APPENDED, never
	// interleaved: a booking-fee or discount line sitting between two ticket lines
	// shifts the index of every ticket line after it, and with it the id of
	// every ticket already minted against an order priced the other way.
	lines: z.array(OrderLineSchema).min(1),
	issuedBy: z
		.string()
		.nullish()
		.describe(
			"the member uid behind a guest order — carried from the guest checkout’s issuedBy onto the order at settle; absent on sales",
		),
	buyerUid: z
		.string()
		.describe(
			"the anonymous Auth uid whose session may read and listen to this order",
		),
	// Excluded: a nested buyerContact (BuyerContactSchema) object — settleOrder stamps
	// the checkout's payload buyer fields onto the order flat, so the member
	// email-lookup index can query buyerEmail directly, with no array/map wildcard.
	buyerEmail: z.string().email(),
	buyerName: z.string().min(1),
	buyerAddress: z
		.string()
		.nullish()
		.describe(
			"the recipient's postal address for the § 14 Abs. 4 Nr. 1 invoice block, one free-text block as the buyer typed it — asked for at checkout only when `needsRecipientAddress(total)` holds, so null is the ordinary, correct state on a Kleinbetragsrechnung and never a missing value to backfill",
		),
	buyerLocale: LocaleSchema,
	channel: ChannelSchema.nullish(),
	// The code this order was bought under, stamped by settleOrder beside
	// `channel` and never inside it: `channel` answers how the buyer arrived,
	// the code answers who sent them, and the two disagree legitimately — one
	// order carries a campaign source and a partner code with neither taking
	// precedence. This is the attribution's only durable home: the checkout
	// the code was typed on is collected 30 days after closedAt, while the
	// order is permanent.
	// The eligible set is frozen beside the code rather than joined from the
	// voucher document at read time: that document is editable and deletable
	// after the order exists, so a live join cannot price an order it no longer
	// describes. What is frozen is not the code's terms but what this order was
	// actually charged — the same class of fact as the stored line amounts
	// beside it. No `.min(1)` on it: resolveVoucher applies with no discount
	// line at all on three paths (a code that only unlocks, a discount clamped
	// to zero, a scope the basket meets in nothing), and a floor would throw at
	// OrderSchema.parse on every one of them.
	// Excluded: what the code took off — the discount lines below carry it, one
	//   per VAT rate, and voucherDiscountCents sums them; a figure stored beside
	//   the lines that produce it is two representations of one fact, free to
	//   disagree, the same reason `fees` keeps no sum beside its entries. The
	//   code itself is not derivable that way: a code that only reveals a hidden
	//   ticket type mints no discount line, so nothing else on the order would
	//   name it — which is the whole asymmetry this field exists for.
	// Excluded: the code's label, who it credits, and the discount's own type
	//   and value — the voucher document under the event carries all four, and
	//   they are read across `code` rather than restated here, where a copy
	//   would be free to contradict a code an operator has since edited
	// Defaults to an explicit null (never an absent key), so `Order["voucher"]`
	// is required-but-nullable rather than optional. The two price derivations
	// below (`perTicketShares` and `refundedVatCents` through it) take a `Pick`
	// of an order, and `Pick` of an OPTIONAL property yields an optional one: a
	// caller projecting only the fields it thinks it needs could drop this field
	// and still compile, whereupon `eligibleTypeIds` reads undefined, the
	// carrier ladder falls through to every line at that rate, and a type-scoped
	// discount spreads across seats it never came off — an order whose total
	// still reconciles while its per-seat figures no longer match what the
	// refund consumer will pay. Required, that projection is a compile error.
	voucher: z
		.object({
			code: z.string(),
			eligibleTypeIds: z
				.array(z.string())
				.nullish()
				.describe(
					"the ticket types this code's discount came off, frozen when the order is stamped — what a refund prices a seat against, since a discount line carries a VAT rate and no type. Written whenever a code is stamped and listing every eligible type even where the code names no scope, so its absence says one thing only: this order carries no record of what its code applied to. Absence read as 'unscoped' instead would price a scoped order's seats against types its discount never touched. Empty where the code applied and took nothing off, which is also an order with no discount line for a seat to be priced against",
				),
		})
		.nullish()
		.default(null)
		.describe(
			"the voucher this order was bought under — stamped once, in the same settle transaction that consumes the code's own use and writes the buyer's redemption; null on every order bought without a code",
		),
	payment: z
		.object({
			provider: PspProviderSchema,
			id: z
				.string()
				.describe(
					"the provider’s order id — the capture key, copied from the checkout’s own payment.id at settle",
				),
			clientToken: z
				.string()
				.describe(
					"a settle-time copy of the checkout’s own clientToken — the widget already confirmed against the checkout’s own-doc listener pre-settle; this copy is a record, not a live delivery channel",
				),
			env: PaymentModeSchema.nullish().describe(
				"the Revolut environment this order was prepared in — verification, capture, and settlement resolve their signing secret, secret key, and host from this stamped value; nullish on an order minted before this field existed, where the money path falls back to the workspace's active mode, then the deployment default",
			),
		})
		.nullish(),
	// Excluded: paymentFailed — a pre-settle decline has no order to live on; its job
	// (offering retry while a decline is live) lives on Checkout.declinedAt instead.
	// Excluded: processedEvents — no order exists at webhook-arrival time; the webhook
	// dedup ledger lives on Checkout instead.
	// Excluded: expiresAt — nothing is held, so there is nothing to expire or sweep.
	orderAccessToken: SecretToken.nullish()
		.default(null)
		.describe(
			"the order’s capability token, written at fulfillment — how the buyer’s own session reaches its orderAccess view and artifacts. Defaults to an explicit null (never an absent key): fulfillmentSweep’s stranded-order scan filters `orderAccessToken == null`, and Firestore’s equality filter matches an explicitly-stored null but never a missing field — the same absence-vs-sentinel trap emailDeliveryStatus’s own default already guards against",
		),
	authorisedAt: z
		.custom<Timestamp>()
		.describe(
			"the order’s own birth stamp — settleOrder creates the order directly in status authorised, inside the same transaction the settle check passes; there is no separate grant event in this model, so the stamp names the transition the order itself undergoes at creation",
		),
	paidAt: z
		.custom<Timestamp>()
		.nullish()
		.describe(
			"capture time — every rendered artifact pins its timestamps here (render-once determinism)",
		),
	handoversAt: z
		.custom<Timestamp>()
		.nullish()
		.describe(
			"when this order's handovers last changed — the document both handover callables write so two concurrent calls on one order serialize. A handover mints a NEW record, and Firestore's transaction contract locks the documents a transaction reads, not the documents a query would later have matched, so two callers minting the same ticket's handover share no written document and nothing orders them; this field is that shared document. Never rendered, and never read for its value",
		),
	refundedAmount: Cents.nullish().describe(
		"cumulative refunded cents — partial refunds are fields, never a status",
	),
	fees: z
		.array(FeeEntrySchema)
		.nullish()
		.default(null)
		.describe(
			"the provider's fee report for this order's payment, its typed entries stored verbatim — the source document, which is why no sum is stored beside it: a stored sum next to stored entries is two representations of one fact, free to disagree, and the displayed figure is the feeCents helper's derivation instead. Null until the provider reports, and null is not an empty report: the fee ties to SETTLEMENT, not capture, so it lands a day or two after the money moves (T+1/T+2) and the reconciler retries until it does. An empty array is the other fact entirely — a transaction that genuinely cost nothing, the arms with no PSP leg, stamped at birth. Written by the reconciler as a top-level update({fees, feeStatus}). A refund neither reverses nor re-reports the entries: acquiring fees are not returned on refund, so the organiser still paid them on an order whose gross has since gone back to the buyer, and the report stands once written. The order's own read rule grants a whole-document get with no field-level mask, so the buyer whose order this is reads what the provider kept along with everything else on the document — the accepted cost of the fee living on the order itself rather than in an admin-only sibling, which would be a second money home and a join on every console read",
		),
	feeStatus: z
		.enum(["pending", "reported", "unavailable"])
		.default("pending")
		.describe(
			"the deferred reconciler's sweep key — the sweep queries `status in [paid, refunded]` ∧ `feeStatus == 'pending'`. The birth write stamps the field explicitly so Firestore's equality query can see it, and the default is what keeps a stored document minted before this field existed parseable on read; because that default is applied on parse and never on disk, such a document is invisible to the sweep's own query, which is why the reconciler carries a second leg that walks orders in birth order and gives them the field. `unavailable` marks an order whose paidAt is more than 30 days old with the fee still unreported. An order born with `payment: null` has no PSP leg: it is stamped with an empty fees array and feeStatus 'reported' at birth and never swept",
		),
	deliveriesComplete: z
		.boolean()
		.default(false)
		.describe(
			"every delivery task done — a flag, never a status, so refunds stay paid→refunded; the buyer’s own-order listener is the reader and may read neither deliveries (member + mfa) nor the Storage listing, so this cannot be derived at the surface that needs it",
		),
	emailDeliveryStatus: DeliveryStatusSchema.default("unknown").describe(
		"stamped `unknown` when the ticket email is sent, then advanced by the Lettermint webhook — one encoding of absence, so the poll queries emailDeliveryStatus == unknown directly",
	),
	invoiceNumber: z
		.string()
		.nullish()
		.describe(
			"the § 14 UStG sequential number, minted once by fulfillReceipt for every priced order regardless of amount — a full invoice is legally valid at any gross total. Null while the receipt task has not run, and permanently on a €0 order, which supplies nothing for consideration and incurs no § 14 obligation",
		),
	ads: z
		.discriminatedUnion("basis", [
			z
				.object({
					basis: z.literal("legitimateInterest"),
					sentAt: z.custom<Timestamp>(),
				})
				.describe(
					"a report sent under legitimate interest — no device was touched, so there is no consent to record beside it",
				),
			z
				.object({
					basis: z.literal("consent"),
					sentAt: z.custom<Timestamp>(),
					consentAt: z
						.custom<Timestamp>()
						.describe("the moment of the buyer's grant the send relied on"),
					consentVersion: z
						.string()
						.describe(
							"the consent-scope version that grant was given against — what the buyer had consented to when the report was made",
						),
				})
				.describe(
					"a report sent under the buyer's consent — the grant is kept here with the purchase because the buyer record it was taken on may later be rewritten by a withdrawal, and the send stays demonstrable after that",
				),
		])
		.nullish()
		.describe(
			"the order's own record of the ad-conversion report it carries and the basis that licensed it — written once by the adConversion delivery task, absent while none was sent",
		),
	updatedBy: z
		.string()
		.nullish()
		.describe("the member uid of the last audited manual fix"),
});
export type Order = z.infer<typeof OrderSchema>;
// A writer that omits a .default()-bearing field (orderAccessToken,
// deliveriesComplete, emailDeliveryStatus) and relies on OrderSchema.parse
// to fill it types its own literal against this z.input, not z.infer's
// output — the same literal typed against Order would demand those fields
// the writer never sets.
export type OrderInput = z.input<typeof OrderSchema>;

/**
 * What each ticket of an order is worth back, in cents — the one price rule
 * both the money path and the console derive from, so a refund's figure and
 * the figure a member reads before confirming it can never differ by a cent.
 *
 * A seat's face share less its share of what a voucher took off, so the shares
 * of an order sum to what the buyer paid for its seats. A face share on a
 * discounted order returns a third party's voucher in cash and eats headroom
 * the order's remaining seats need to come back at all.
 *
 * Ticket lines only. A booking-fee or discount line buys no seat, so it contributes
 * no ticket share; folding either into this map would price a ticket at money
 * that never bought one.
 *
 * Per line: the order's tickets of that line's typeId, sorted by ticketId
 * ascending — the only stable order the documents carry. Each gets
 * `floor(net / qty)`, and the last one carries the remainder as well, so the
 * shares of a line sum back to that net exactly however indivisible it is.
 * `amount` is the line's own charged total, with a tier price the catalog
 * resolved already inside it and never `unitPrice × qty`, which is exactly why
 * the floor-and-remainder rule exists rather than a per-unit price read
 * straight off the line.
 *
 * The discount reaches a line through its rate: each discount line's cents
 * apportion across the ticket lines at that rate the code was eligible for —
 * `voucher.eligibleTypeIds` where the order records it, every line at that
 * rate where it does not, and every ticket line where no line carries that
 * rate at all, since cents landing nowhere leave the seats summing to more
 * than was paid. § 10 Abs. 1 UStG is what makes the rate the unit: a Rabatt
 * reduces the Entgelt of the supplies it relates to, which is why the discount
 * splits by rate in the first place. A line carrying no discount is untouched,
 * so an order bought without a code prices at face throughout.
 *
 * A type-scoped code on an order recording no eligible set can put a few euros
 * on the wrong seat of that order. It can never move the wrong total: the
 * discount is known to the cent and the seats always sum to what was paid.
 *
 * Tickets are supplied by the caller, never read here: they are their own
 * documents under `events/{eventId}/tickets/{ticketId}`, joined to an order by
 * their own `orderId` field rather than by nesting, and
 * this function does no I/O — a Cloud Function and a browser view both call it.
 *
 * A line whose typeId's ticket count disagrees with its `qty` is not
 * priceable, and guessing a share for it would move the wrong money: every
 * ticket of such a line is simply absent from the returned map. That absence
 * IS the caller's mismatch signal — a caller pricing a set of tickets treats
 * the first `shares.get(ticketId) === undefined` as `lineTicketMismatch` and
 * refuses whole. Two lines sharing one typeId fall into the same case by
 * construction, since neither line's qty can equal that type's whole count.
 *
 * An order whose discount takes off more than the lines it comes off were
 * charged prices no seat at all — the map comes back empty and every caller's
 * own absence refusal carries it. The mint is what holds that off:
 * `resolveVoucher` clamps a discount to the subtotal of the lines it applies
 * to — a percentage of that subtotal, a fixed amount to no more than it — so
 * no checkout produces the shape. `OrderSchema` admits it regardless, and a
 * seat priced below zero is cash paid back on an order whose own lines
 * disagree — a fact a caller must reach as a refusal rather than receive as a
 * figure.
 */
export function perTicketShares(
	order: Pick<Order, "lines" | "voucher">,
	tickets: readonly (Pick<Ticket, "typeId"> & { id: string })[],
): Map<string, number> {
	const ticketLines = order.lines.filter((line) => line.kind === "ticket");
	const discountByRate = new Map<number, number>();
	for (const line of order.lines)
		if (line.kind === "discount")
			discountByRate.set(
				line.vatRate,
				(discountByRate.get(line.vatRate) ?? 0) - line.amount,
			);
	const eligibleTypeIds = order.voucher?.eligibleTypeIds;
	const discountByType = new Map<string, number>();
	for (const [vatRate, cents] of discountByRate) {
		const atRate = ticketLines.filter((line) => line.vatRate === vatRate);
		const eligible =
			eligibleTypeIds == null
				? atRate
				: atRate.filter((line) => eligibleTypeIds.includes(line.typeId));
		// The first set that can carry the rate's cents, which takes having
		// been charged something for them to come off: a set of lines charged
		// nothing takes nothing off however many lines it holds. The last
		// rung is the one that has to exist rather than the one that is
		// reached — a rate no ticket line carries has nowhere of its own to
		// land, and cents landing nowhere leave every seat priced above what
		// the buyer paid for it.
		const carriers = [eligible, atRate, ticketLines].find(
			(candidate) =>
				candidate.reduce((subtotal, line) => subtotal + line.amount, 0) > 0,
		);
		// Nothing on the order was charged anything for a discount to come
		// off — the order's lines disagree, and no seat of it is priceable.
		if (carriers === undefined) return new Map();
		for (const share of apportionByLine(cents, carriers))
			discountByType.set(
				share.typeId,
				(discountByType.get(share.typeId) ?? 0) + share.cents,
			);
	}

	const shares = new Map<string, number>();
	for (const line of ticketLines) {
		const net = line.amount - (discountByType.get(line.typeId) ?? 0);
		// More came off the line than it was charged: the same disagreement,
		// and a share below zero is money the caller would pay out on it.
		if (net < 0) return new Map();
		const ids = tickets
			.filter((ticket) => ticket.typeId === line.typeId)
			.map((ticket) => ticket.id)
			.sort();
		if (ids.length !== line.qty) continue;
		const share = Math.floor(net / line.qty);
		for (const [index, id] of ids.entries()) {
			shares.set(
				id,
				index === ids.length - 1 ? net - (line.qty - 1) * share : share,
			);
		}
	}
	return shares;
}

/**
 * Net + VAT split from a gross amount at one rate — the § 14 invoice's own
 * split: round the NET, derive the VAT as the remainder, so net + vat always
 * reconstructs the gross exactly. § 10 Abs. 1 Satz 2 UStG makes the Entgelt
 * what the buyer gives INCLUDING the tax, so the VAT is extracted out of the
 * gross (herausgerechnet), never added on top of it.
 *
 * The rounding DIRECTION is load-bearing: the round-VAT variant
 * (`round(amount × rate / (100 + rate))`) agrees at 7 % and 19 % but diverges
 * at 12 % on gross multiples of 14 cents and at 20 % on every odd multiple of
 * 3 cents — one cent apart, which on a legal document is a different
 * document. One formula, one home: the invoice template's own line split
 * delegates here, and every aggregate that must agree with the printed
 * invoice sums this function's output, never a second arithmetic.
 */
export function splitGross(
	amountCents: number,
	vatRate: number,
): { netCents: number; vatCents: number } {
	const netCents = Math.round(amountCents / (1 + vatRate / 100));
	return { netCents, vatCents: amountCents - netCents };
}

/**
 * A cents amount spread across the VAT rates it is owed at, in proportion to
 * what each rate carries — the one apportionment rule the booking fee and a
 * voucher's discount both put on an order, so the two can never round a mixed
 * basket a cent apart on the same § 14 invoice.
 *
 * `lines` are the supplies the amount relates to; their per-rate subtotals are
 * the proportions. A rate carrying nothing (or carrying less than the discounts
 * already taken off it) is neither charged at nor counted in what the others
 * are measured against, so the proportions are always of money someone
 * actually pays.
 *
 * Each rate takes the floor of its exact share, then the cents left over go one
 * apiece to the largest fractional remainders — ties to the lower rate, since
 * the ascending-rate sort is stable — so the parts sum back to `amountCents`
 * exactly however indivisible it is. A rate whose share rounds away is absent
 * from the result rather than present at zero: a zero-cent part is not a part,
 * and both line kinds built from this refuse one.
 *
 * Always a positive cents figure, ascending by rate. A caller taking money off
 * rather than adding it passes the amount positive and flips the sign on the
 * lines it builds — the rounding may not depend on the direction, or the two
 * callers stop agreeing.
 */
export function apportionByRate(
	amountCents: number,
	lines: readonly Pick<OrderLine, "vatRate" | "amount">[],
): { vatRate: number; cents: number }[] {
	if (amountCents <= 0) return [];
	const subtotalByRate = new Map<number, number>();
	for (const line of lines)
		subtotalByRate.set(
			line.vatRate,
			(subtotalByRate.get(line.vatRate) ?? 0) + line.amount,
		);
	const carried = [...subtotalByRate.entries()]
		.filter(([, subtotal]) => subtotal > 0)
		.sort(([a], [b]) => a - b);
	let basis = 0;
	for (const [, subtotal] of carried) basis += subtotal;
	if (basis <= 0) return [];

	const shares = carried.map(([vatRate, subtotal]) => {
		const exact = (amountCents * subtotal) / basis;
		return { vatRate, cents: Math.floor(exact), remainder: exact % 1 };
	});
	let residue =
		amountCents - shares.reduce((sum, share) => sum + share.cents, 0);
	for (const share of [...shares].sort((a, b) => b.remainder - a.remainder)) {
		if (residue <= 0) break;
		share.cents += 1;
		residue -= 1;
	}
	return shares
		.filter((share) => share.cents > 0)
		.map(({ vatRate, cents }) => ({ vatRate, cents }));
}

/**
 * A cents amount spread across the ticket lines it comes off, in proportion to
 * what each line costs — how a discount stated per VAT rate reaches the
 * individual supplies that rate is carried by, which is what pricing one seat
 * of a discounted order turns on.
 *
 * Keyed on `typeId`, and that keying is the whole reason it stands beside
 * `apportionByRate` rather than calling it: that function's accumulator is the
 * VAT rate, so several lines at one rate go in as several and come out as one
 * entry with nothing left saying how much of it each line carried.
 *
 * The arithmetic is the same one, cent for cent — each line takes the floor of
 * its exact share, the cents left over go one apiece to the largest fractional
 * remainders, and a tie goes to the lower `typeId`, since line order carries
 * no stable meaning the way an ascending rate does. The parts sum back to
 * `amountCents` exactly however indivisible it is. A line carrying nothing —
 * or carrying less than what has already come off it — is neither charged at
 * nor counted in what the others are measured against, so the proportions are
 * always of money someone actually pays, and a line whose share rounds away is
 * absent from the result rather than present at zero.
 *
 * Handed nothing but such lines, the answer is nothing: there is no basis to
 * apportion against, and the cents stay with the caller, who owns where they
 * land. A caller spreading a discount answers for that by choosing a set of
 * lines with a subtotal above zero, since cents that reach no line leave the
 * supplies summing to more than was paid for them.
 *
 * Always a positive cents figure, ascending by `typeId`. A caller taking money
 * off passes the amount positive, the same way `apportionByRate`'s do.
 */
export function apportionByLine(
	amountCents: number,
	lines: readonly Pick<TicketLine, "typeId" | "amount">[],
): { typeId: string; cents: number }[] {
	if (amountCents <= 0) return [];
	const subtotalByType = new Map<string, number>();
	for (const line of lines)
		subtotalByType.set(
			line.typeId,
			(subtotalByType.get(line.typeId) ?? 0) + line.amount,
		);
	const carried = [...subtotalByType.entries()]
		.filter(([, subtotal]) => subtotal > 0)
		.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
	let basis = 0;
	for (const [, subtotal] of carried) basis += subtotal;
	if (basis <= 0) return [];

	const shares = carried.map(([typeId, subtotal]) => {
		const exact = (amountCents * subtotal) / basis;
		return { typeId, cents: Math.floor(exact), remainder: exact % 1 };
	});
	let residue =
		amountCents - shares.reduce((sum, share) => sum + share.cents, 0);
	for (const share of [...shares].sort((a, b) => b.remainder - a.remainder)) {
		if (residue <= 0) break;
		share.cents += 1;
		residue -= 1;
	}
	return shares
		.filter((share) => share.cents > 0)
		.map(({ typeId, cents }) => ({ typeId, cents }));
}

/**
 * The VAT an order owes on its full gross, in cents — the one extraction rule
 * the console's revenue breakdown and an order's own detail both read, so the
 * figure an operator sees against one order and the figure they see summed
 * across an event can never disagree by a cent with the § 14 invoice.
 *
 * Every line of every kind, deliberately: an order owes VAT on the booking fee it
 * charged and owes less of it for the discount it gave, and each carries the
 * rate that governs it, so summing across kinds is the whole derivation and no
 * branch is needed. A discount line's negative amount splits to a negative
 * net and a negative VAT, which is the reduction the invoice prints.
 *
 * Per line: `splitGross(line.amount, line.vatRate).vatCents`, then summed —
 * a line's figure is the one the invoice prints, and the Finanzverwaltung
 * accepts per-line and summed-base rounding alike. `vatRate` is read off each
 * line and never assumed — this platform's own default is 7 (§ 12 Abs. 2
 * Nr. 7 lit. a UStG, admission to concerts and comparable performances), a
 * type that does not qualify sells at another rate, and one order mixing
 * rates is an ordinary case rather than an edge. A €0 line contributes €0.
 *
 * Refunds are deliberately not this function's concern: the VAT of a
 * refunded portion is a per-line attribution question `refundedVatCents`
 * answers (or refuses), never a scaling applied to this total.
 *
 * Excluded: the governing rate itself. Whether one rate governs — the question
 * a rendered explanation asks before it names a percentage — is a set over
 * `lines[].vatRate`, which is how the checkout's own price disclosure already
 * asks it; deriving it here would be a second function wearing one name.
 *
 * No I/O, like `perTicketShares`: a Cloud Function and a browser view both
 * call it.
 */
export function vatCents(order: Pick<Order, "lines">): number {
	let vat = 0;
	for (const line of order.lines) {
		vat += splitGross(line.amount, line.vatRate).vatCents;
	}
	return vat;
}

/**
 * The VAT of an order's refunded portion, in cents — attributed to lines the
 * way the refund's own correction document puts it in the books: each
 * cancelled ticket carries its `perTicketShares` price net of the discount its
 * seat bore, the shares sum per line typeId, and each line's refunded cents
 * split at that line's own `vatRate` via `splitGross`.
 *
 * `refundedAmount` equal to `Σ lines[].amount` — every line of every kind,
 * the booking fee and the discount among them — is the whole gross back, and
 * the figure is then `vatCents(order)` exactly: the per-rate reading the § 14
 * invoice prints, which on a discounted order sits a cent from what the
 * attribution below reads. That equality is the whole of the test — no ticket
 * is consulted for it, and every seat returned is not the same thing. An
 * order carrying a booking fee never reaches it by returning seats, since the
 * fee buys none and the cancelled shares stop short of the order's own gross;
 * the attribution is what prices that refund, and answering the whole order's
 * VAT for it would assert the fee's VAT as repaid while it stayed with the
 * operator — the § 14c exposure. The attribution carries the
 * per-line-versus-per-rate cent `splitGross` already carries.
 *
 * `tickets` is ALL the order's tickets, not just the cancelled ones —
 * `perTicketShares` prices a line only when its full ticket set matches its
 * qty. No I/O, like the siblings above.
 *
 * Returns null when the refund is not attributable: a cancelled ticket with
 * no share (its line is unpriceable), or attributed cents that do not sum to
 * `refundedAmount` (a PSP-initiated partial with no cancelled tickets). A
 * guessed rate split is the § 14c wrong-VAT exposure, so the caller contract
 * is: null means "exclude this order whole from any aggregate and count it",
 * never guess.
 */
export function refundedVatCents(
	order: Pick<Order, "lines" | "refundedAmount" | "voucher">,
	tickets: readonly (Pick<Ticket, "typeId" | "status"> & { id: string })[],
): number | null {
	const refunded = order.refundedAmount ?? 0;
	if (refunded <= 0) return 0;
	if (refunded === order.lines.reduce((sum, line) => sum + line.amount, 0))
		return vatCents(order);
	const shares = perTicketShares(order, tickets);
	const refundedByType = new Map<string, number>();
	for (const ticket of tickets) {
		if (ticket.status !== "cancelled") continue;
		const share = shares.get(ticket.id);
		if (share === undefined) return null;
		refundedByType.set(
			ticket.typeId,
			(refundedByType.get(ticket.typeId) ?? 0) + share,
		);
	}
	let attributed = 0;
	let vat = 0;
	for (const line of order.lines.filter((line) => line.kind === "ticket")) {
		const refundedLineCents = refundedByType.get(line.typeId);
		if (refundedLineCents === undefined) continue;
		attributed += refundedLineCents;
		vat += splitGross(refundedLineCents, line.vatRate).vatCents;
	}
	if (attributed !== refunded) return null;
	return vat;
}

/**
 * What the provider kept to process this order's payment, in cents — the one
 * derivation every surface reads, summed from the order's stored fee entries
 * so the figure can never disagree with the report it derives from. Null
 * while the provider has not reported (`fees` null), and null is not zero: a
 * reader that renders an unreported fee as €0 overstates what the organiser
 * keeps by exactly the fee. An empty report sums to 0 — a transaction that
 * genuinely cost nothing — so the two facts owe different words wherever
 * either is shown. No I/O, like the siblings above.
 */
export function feeCents(order: Pick<Order, "fees">): number | null {
	if (order.fees == null) return null;
	return order.fees.reduce((sum, entry) => sum + entry.amountCents, 0);
}

/**
 * What a voucher took off this order, in cents — summed from the discount
 * lines themselves, positive, so the figure and the lines it comes from can
 * never disagree. `order.voucher` names which code did it; this is what that
 * code cost. No I/O, like the siblings above.
 *
 * A voucher's discount only. A volume discount is already inside a ticket
 * line's own charged total and mints no line of its own, so it is outside this
 * figure and outside any code's attribution.
 *
 * 0 on an order whose code only revealed a hidden ticket type: it carries a
 * stamped code and no discount line at all, which is why the code is stamped
 * and the amount is derived.
 */
export function voucherDiscountCents(order: Pick<Order, "lines">): number {
	return order.lines
		.filter((line) => line.kind === "discount")
		.reduce((takenOff, line) => takenOff - line.amount, 0);
}

/**
 * Whether a seat of this order gives back its printed price or something less,
 * and why — the one verdict every refund surface branches on, so the console,
 * the refund consumer, the § 14 correction and the buyer's own list cannot
 * disagree about one order. Each already holds the order, and the answer is
 * in the order's own lines: the voucher document is editable and deletable
 * after the order exists, so nothing here reads it.
 *
 * The discount lines are the whole question, and they answer all of it. An
 * order carrying none pays back face, whether or not a code is stamped on it
 * — a code that only revealed a hidden ticket type took no money off
 * anything. An order carrying one pays back net: that line names the code
 * that took the money, `voucherDiscountCents` sums what the lines say it came
 * to, and each figure is read from the one place that produces it rather than
 * restated beside it.
 *
 * The code is read off the line that took the money rather than off the
 * order's stamp of it: settle writes both in one transaction out of one
 * `resolveVoucher`, so the two say the same thing, and the line is the one
 * whose existence this arm turns on.
 */
export type RefundPricing =
	| { kind: "faceValue" }
	| { kind: "voucherNet"; code: string; discountCents: number };

export function refundPricing(order: Pick<Order, "lines">): RefundPricing {
	const discountLine = order.lines.find((line) => line.kind === "discount");
	if (discountLine === undefined) return { kind: "faceValue" };
	return {
		kind: "voucherNet",
		code: discountLine.code,
		discountCents: voucherDiscountCents(order),
	};
}

export const Order = entity<Actor>()({
	schema: OrderSchema,
	actions: ["read", "list"] as const,
})
	.pipe(({ can, hasPermission, and, pathVar }) => [
		// Buyer: reads and listens to their own order — the outcome mechanism
		// once the checkout settles. Not list-safe (touches data.buyerUid) —
		// the emitter downgrades the combined 'read' bucket to `allow get:`
		// only, which is fine: the buyer always knows their own orderId from
		// the checkout flow.
		can("read").when((data, me) => data.buyerUid.eq(me.uid)),
		// Guest-issuer: reads the outcome of a guest order they personally
		// issued — same shape as the buyer clause above, just keyed on
		// issuedBy instead of buyerUid. Not list-safe either (touches
		// data.issuedBy), so a tickets.guests.issue-only actor gets their own
		// submission's outcome without needing tickets.reports.view; the
		// browsable guest-order list still needs it, since a self-scoped
		// get can't service a list query.
		can("read").when((data, me) => data.issuedBy.eq(me.uid)),
		// Member: lookup + attendee-list support. list-safe (hasPermission +
		// mfa only), so 'list' is declared separately here.
		can("read").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.reports.view", {
					event: pathVar("eventId"),
				}),
				me.mfa.eq(true),
			),
		),
		can("list").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.reports.view", {
					event: pathVar("eventId"),
				}),
				me.mfa.eq(true),
			),
		),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/events/{eventId}/orders/{orderId}",
		}),
	);

// The canonical delivery-task vocabulary — the single source every consumer
// (the marker-doc id itself, fulfillOrder's own marker-scaffold loop,
// fulfillmentSweep's per-task dispatch) derives from, so the set is spelled
// once and never hand-copied a second time. Every member but adConversion
// produces a buyer-facing artifact; adConversion is the order's own ad
// report (`ads` above) and never counts toward deliveriesComplete.
export const DeliveryTaskNameSchema = z
	.enum([
		"tickets",
		"ticketFiles",
		"email",
		"walletApple",
		"walletGoogle",
		"receipt",
		"orderAccess",
		"adConversion",
	])
	.describe(
		"which fulfillment artifact this delivery task produces — repeated here as a field (not read off the doc's own id) because a collectionGroup query can filter on a field but not on a document's own leaf id",
	);
export type DeliveryTaskName = z.infer<typeof DeliveryTaskNameSchema>;

// What a fulfillment email did with the order's rendered ticket files. Named
// here rather than inlined on the field below because two readers outside this
// file decide on it: deliveries/advance.webhook.ts, which owes a second mail
// only on `none`, and @momo/ticket-template's email body, which states either
// that the files ride along or that they wait at the order-access link.
export const TicketFilesAttachmentSchema = z.enum([
	"attached",
	"overBudget",
	"none",
]);
export type TicketFilesAttachment = z.infer<typeof TicketFilesAttachmentSchema>;

/** Whose Lettermint account sends an organization's mail: its own, or the
 *  deployment's (`platform/mail`). */
export const SendingAccountSchema = z.enum(["organization", "platform"]);
export type SendingAccount = z.infer<typeof SendingAccountSchema>;

// Two counters, two jobs — hence two names that cannot be typo'd into each
// other. A retry (the fulfillment sweep re-running a pending/failed
// delivery task) reuses the SAME sendNumber, so the retry can never
// double-execute; a resend (member-initiated) increments it, creating a
// genuinely new idempotency key.
export const DeliveryTaskSchema = z.object({
	task: DeliveryTaskNameSchema,
	status: DeliveryTaskStatusSchema.default("pending"),
	retries: z
		.number()
		.int()
		.min(0)
		.default(0)
		.describe(
			"fulfillment-sweep retry count — bounded, then failed + ops alert",
		),
	sendNumber: z
		.number()
		.int()
		.min(1)
		.default(1)
		.describe(
			"the idempotency component — the email key is order:{orderId}:email:{sendNumber}; a resend increments it, a retry reuses it",
		),
	providerMessageId: z.string().nullish(),
	ticketFilesAttachment: TicketFilesAttachmentSchema.nullish().describe(
		"what the send that flipped the email marker done did with the order's rendered ticket files — attached them, dropped them for the provider's message-size ceiling, or found none stored yet. Nullish on every non-email task and on markers written before the field existed. Read by deliveries/advance.webhook.ts: only the `none` reading owes the buyer a second mail once those files land, and the other two readings are what makes that second mail fire exactly once",
	),
	sendAccount: z
		.object({
			sendNumber: z.number().int().min(1),
			account: SendingAccountSchema,
		})
		.strict()
		.nullish()
		.describe(
			"the Lettermint account an email generation (sendNumber) first sent on, pinned before its first send so every retry of that generation sends on the same account — the provider scopes the idempotency key to one project, so a retry crossing from the deployment's account to the organization's own (or back) would not be deduplicated. A resend's new sendNumber pins afresh. Nullish on every non-email task",
		),
	deliveryStatus: DeliveryStatusSchema.nullish().describe(
		"the task’s own copy of what the provider reported — nullish, unlike Order.emailDeliveryStatus, because nothing QUERIES this one: the collection-group index over deliveries is on `task` + `status`. The order-level field is the queried sentinel and therefore carries no null",
	),
});
export type DeliveryTask = z.infer<typeof DeliveryTaskSchema>;
// z.input, not z.infer: status/retries/sendNumber all carry a .default(), so
// a writer scaffolding a fresh marker (fulfillOrder's own marker loop) hands
// the schema a looser shape than what a parse returns.
export type DeliveryTaskInput = z.input<typeof DeliveryTaskSchema>;

// Member lookup read only — the email resend write pattern (sendNumber
// + 1, status: 'pending' on the email task doc) lives entirely in root
// package.json's ican.extraMatches: the increment is relative to the
// doc's own pre-write value, which .writes(...)'s literal/token-only
// value map cannot express.
export const DeliveryTask = entity<Actor>()({
	schema: DeliveryTaskSchema,
	actions: ["read"] as const,
})
	.pipe(({ can, hasPermission, and, pathVar }) => [
		can("read").when((_data, me) =>
			and(
				hasPermission(me.permissions, "tickets.reports.view", {
					event: pathVar("eventId"),
				}),
				me.mfa.eq(true),
			),
		),
	])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/events/{eventId}/orders/{orderId}/deliveries/{task}",
		}),
	);
